// ItemDetailsPage.js - Component to display detailed information about collection items

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { moveToCollection } from '../utils/wishlistUtils';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Divider,
  Grid,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  EmojiEvents as EmojiEventsIcon,
  AttachMoney as AttachMoneyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import ChatIcon from '@mui/icons-material/Chat';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import AppBarHeader from './AppBarHeader';
import EbayPriceValue from './EbayPriceValue';
import AlternativeMarketValue from './AlternativeMarketValue';
import EditDialog from './shared/EditDialog';
import gameEditFields from './games/gameEditFields';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../axiosInstance';
import ReviewList from './reviews/ReviewList';
import ItemGallery from './gallery/ItemGallery';

/**
 * Component to display detailed information about an item (game, console, or lego)
 */
const ItemDetailsPage = () => {
  const { itemType, itemId } = useParams();
  const ITEM_TYPE_TO_COLLECTION = {
    game: 'games', console: 'consoles', lego: 'lego',
    movie: 'movies', music: 'music', book: 'books', 'board-game': 'board-games',
    'trading-card': 'trading-cards', manga: 'manga', comic: 'comics',
  };
  const collectionType = ITEM_TYPE_TO_COLLECTION[itemType] || itemType;
  const ITEM_TYPE_TO_CHAT_ROOM = {
    game: 'retro-gaming', console: 'retro-gaming', lego: 'lego',
    movie: 'movies', music: 'music', book: 'books', 'board-game': 'board-games',
  };
  const chatRoom = ITEM_TYPE_TO_CHAT_ROOM[itemType] || 'lobby';
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { featureFlags } = useFeatureFlags();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Get user info from token
  const token = localStorage.getItem('token');
  let userId = null;
  let isAdmin = false;
  
  if (token) {
    try {
      const decodedToken = jwtDecode(token);
      userId = decodedToken.id;
      isAdmin = decodedToken.isAdmin;
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }

  // Fetch item details from API or use state passed from collection page
  useEffect(() => {
    const fetchItemDetails = async (type, id) => {
      setLoading(true);
      try {
        // Check if this is a wishlist item
        const isWishlistItem = location.state?.fromWishlist || false;
        console.log('Is wishlist item (fetch):', isWishlistItem, 'Type:', type, 'ID:', id);
        
        // Construct the appropriate endpoint based on whether it's a wishlist item
        const normalizedType = (type === 'movies') ? 'movie' : type;

        const buildWishlistEndpoint = () => {
          switch (normalizedType) {
            case 'game':
              return `/game-wishlist/${id}`;
            case 'console':
              return `/console-wishlist/${id}`;
            case 'lego':
              return `/lego-wishlist/item/${id}`;
            case 'movie':
              return `/movie-wishlist/${id}`;
            case 'music':
              return `/music-wishlist/${id}`;
            case 'book':
              return `/book-wishlist/${id}`;
            case 'board-game':
              return `/board-game-wishlist/${id}`;
            case 'trading-card':
              return `/trading-cards/${id}`;
            case 'manga':
              return `/manga/${id}`;
            case 'comic':
              return `/comics/${id}`;
            default:
              return `/${normalizedType}-wishlist/${id}`;
          }
        };

        const buildCollectionEndpoint = () => {
          switch (normalizedType) {
            case 'game':
              return `/games/${id}`;
            case 'console':
              return `/consoles/${id}`;
            case 'lego':
              return `/lego/${id}`;
            case 'movie':
              return `/movies/${id}`;
            case 'music':
              return `/music/${id}`;
            case 'book':
              return `/books/${id}`;
            case 'board-game':
              return `/board-games/${id}`;
            case 'manga':
              return `/manga/${id}`;
            case 'trading-card':
              return `/trading-cards/${id}`;
            case 'comic':
              return `/comics/${id}`;
            default:
              return `/${normalizedType}s/${id}`;
          }
        };

        const endpoint = isWishlistItem ? buildWishlistEndpoint() : buildCollectionEndpoint();
        console.log(`Using ${isWishlistItem ? 'wishlist' : 'collection'} endpoint: ${endpoint}`);
        
        console.log(`Fetching item details from endpoint: ${endpoint}`);
        const response = await axiosInstance.get(endpoint);
        console.log(`Successfully fetched ${type} details:`, response.data);
        setItem(response.data);
        setError(null);
      } catch (err) {
        console.error(`Error fetching ${type} details:`, err);
        console.error('Error response:', err.response?.data || 'No response data');
        console.error('Error status:', err.response?.status || 'No status code');
        console.error('Request URL:', err.config?.url || 'URL not available');
        setError(`Failed to load ${type} details. Please try again or go back to your collection.`);
        setItem(null);
      }
      setLoading(false);
    };

    if (itemType && itemId) {
      // Show nav state instantly while fresh data loads in background
      if (location.state && location.state.item) {
        setItem(location.state.item);
        setLoading(false);
      }
      fetchItemDetails(itemType, itemId);
    } else {
      setError("Item details could not be loaded. Essential parameters (itemType or itemId) are missing.");
      setLoading(false);
    }
  }, [itemType, itemId, location.state]);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Handle back button click
  const handleBack = () => {
    console.log('handleBack called - current path:', location.pathname);
    console.log('Location state:', location.state);
    
    // Use the same wishlist detection as the button text
    const isWishlistPage = location.state?.fromWishlist || false;
    
    console.log('Is wishlist page (in handleBack):', isWishlistPage);
    
    if (isWishlistPage) {
      // Handle different wishlist types
      console.log('Navigating to wishlist for type:', itemType);
      
      // Use a more direct navigation approach to prevent crashes
      // Force a hard navigation to the wishlist page without any state
      // This avoids any issues with React Router's history stack
      
      // Determine the correct route - make sure these match the routes in App.js
      let route = '/';
      switch(itemType) {
        case 'movie':
          route = '/wishlist/movies';
          break;
        case 'game':
          route = '/wishlist/games';
          break;
        case 'console':
          route = '/wishlist/consoles';
          break;
        case 'lego':
          route = '/wishlist/legos'; // Fixed: changed from '/wishlist/lego' to '/wishlist/legos' to match App.js route
          break;
        case 'music':
          route = '/wishlist/music';
          break;
        case 'book':
          route = '/wishlist/books';
          break;
        case 'board-game':
          route = '/wishlist/board-games';
          break;
        case 'trading-card':
          route = '/wishlist/trading-cards';
          break;
        case 'manga':
          route = '/wishlist/manga';
          break;
        case 'comic':
          route = '/wishlist/comics';
          break;
        default:
          route = '/';
      }

      navigate(route);
    } else {
      navigate(-1);
    }
  };

  // Handle move to collection button click
  const handleMoveToCollection = async () => {
    try {
      // Show loading message
      enqueueSnackbar('Moving item to collection...', { variant: 'info' });
      
      // Call the moveToCollection function from wishlistUtils.js
      await moveToCollection('movie-wishlist', itemId);
      
      // Show success message
      enqueueSnackbar('Item moved to collection successfully!', { variant: 'success' });
      
      // Navigate back to the wishlist
      navigate('/wishlist/movies');
    } catch (error) {
      console.error('Error moving item to collection:', error);
      enqueueSnackbar(`Error moving item to collection: ${error.message}`, { variant: 'error' });
    }
  };

  // Open edit dialog for the current item
  const handleEdit = () => {
    try {
      console.log(`Editing ${itemType} with ID: ${itemId}`);
      // Open the edit dialog directly instead of navigating away
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Edit error:', error);
      enqueueSnackbar(`Error opening edit dialog: ${error.message}`, { variant: 'error' });
    }
  };
  
  // Handle saving edited item
  const handleSaveEdit = async (updatedItem) => {
    try {
      console.log('Saving edited item:', updatedItem);
      
      // Check if this is a wishlist item
      const isWishlistItem = location.state?.fromWishlist || false;
      console.log('Is wishlist item:', isWishlistItem);
      
      // Use the correct endpoint based on item type and whether it's a wishlist item
      const normalizedType = (itemType === 'movies') ? 'movie' : itemType;

      const buildWishlistEndpoint = () => {
        switch (normalizedType) {
          case 'game':
            return `/game-wishlist/${itemId}`;
          case 'console':
            return `/console-wishlist/${itemId}`;
          case 'lego':
            return `/lego-wishlist/item/${itemId}`;
          case 'movie':
            return `/movie-wishlist/${itemId}`;
          case 'music':
            return `/music-wishlist/${itemId}`;
          case 'book':
            return `/book-wishlist/${itemId}`;
          case 'board-game':
            return `/board-game-wishlist/${itemId}`;
          case 'trading-card':
            return `/trading-cards/${itemId}`;
          case 'manga':
            return `/manga/${itemId}`;
          case 'comic':
            return `/comics/${itemId}`;
          default:
            return `/${normalizedType}-wishlist/${itemId}`;
        }
      };

      const buildCollectionEndpoint = () => {
        switch (normalizedType) {
          case 'game':
            return `/games/${itemId}`;
          case 'console':
            return `/consoles/${itemId}`;
          case 'lego':
            return `/lego/${itemId}`;
          case 'movie':
            return `/movies/${itemId}`;
          case 'music':
            return `/music/${itemId}`;
          case 'book':
            return `/books/${itemId}`;
          case 'board-game':
            return `/board-games/${itemId}`;
          case 'manga':
            return `/manga/${itemId}`;
          case 'trading-card':
            return `/trading-cards/${itemId}`;
          case 'comic':
            return `/comics/${itemId}`;
          default:
            return `/${normalizedType}s/${itemId}`;
        }
      };

      const endpoint = isWishlistItem ? buildWishlistEndpoint() : buildCollectionEndpoint();
      console.log('Using endpoint:', endpoint);
      const response = await axiosInstance.put(endpoint, updatedItem);
      
      // Update the item in state
      setItem(response.data);
      setEditDialogOpen(false);
      
      enqueueSnackbar(`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} updated successfully!`, { variant: 'success' });
    } catch (error) {
      console.error(`Error updating ${itemType}:`, error);
      enqueueSnackbar(`Error updating ${itemType}: ${error.message}`, { variant: 'error' });
    }
  };

  // Handle delete button click
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  // Handle cancel button in delete dialog
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Handle confirm button in delete dialog
  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Check if this is a wishlist item
      const isWishlistItem = location.state?.fromWishlist || false;
      console.log('Is wishlist item (delete):', isWishlistItem);
      
      // Use the correct endpoint based on item type and whether it's a wishlist item
      const normalizedType = (itemType === 'movies') ? 'movie' : itemType;

      const buildWishlistEndpoint = () => {
        switch (normalizedType) {
          case 'game':
            return `/game-wishlist/${itemId}`;
          case 'console':
            return `/console-wishlist/${itemId}`;
          case 'lego':
            return `/lego-wishlist/item/${itemId}`;
          case 'movie':
            return `/movie-wishlist/${itemId}`;
          case 'music':
            return `/music-wishlist/${itemId}`;
          case 'book':
            return `/book-wishlist/${itemId}`;
          case 'board-game':
            return `/board-game-wishlist/${itemId}`;
          case 'trading-card':
            return `/trading-cards/${itemId}`;
          case 'manga':
            return `/manga/${itemId}`;
          case 'comic':
            return `/comics/${itemId}`;
          default:
            return `/${normalizedType}-wishlist/${itemId}`;
        }
      };

      const buildCollectionEndpoint = () => {
        switch (normalizedType) {
          case 'game':
            return `/games/${itemId}`;
          case 'console':
            return `/consoles/${itemId}`;
          case 'lego':
            return `/lego/${itemId}`;
          case 'movie':
            return `/movies/${itemId}`;
          case 'music':
            return `/music/${itemId}`;
          case 'book':
            return `/books/${itemId}`;
          case 'board-game':
            return `/board-games/${itemId}`;
          case 'manga':
            return `/manga/${itemId}`;
          case 'trading-card':
            return `/trading-cards/${itemId}`;
          case 'comic':
            return `/comics/${itemId}`;
          default:
            return `/${normalizedType}s/${itemId}`;
        }
      };

      const endpoint = isWishlistItem ? buildWishlistEndpoint() : buildCollectionEndpoint();
      console.log('Using endpoint for delete:', endpoint);
      await axiosInstance.delete(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      enqueueSnackbar(`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} removed successfully!`, { variant: 'success' });
      setDeleteDialogOpen(false);

      const getReturnRoute = () => {
        if (isWishlistItem) {
          switch (itemType) {
            case 'movie':
              return '/wishlist/movies';
            case 'game':
              return '/wishlist/games';
            case 'console':
              return '/wishlist/consoles';
            case 'lego':
              return '/wishlist/legos';
            case 'music':
              return '/wishlist/music';
            case 'book':
              return '/wishlist/books';
            case 'board-game':
              return '/wishlist/board-games';
            case 'trading-card':
              return '/wishlist/trading-cards';
            case 'manga':
              return '/wishlist/manga';
            case 'comic':
              return '/wishlist/comics';
            default:
              return '/';
          }
        }

        switch (itemType) {
          case 'game':
            return '/games';
          case 'console':
            return '/consoles';
          case 'lego':
            return '/lego';
          case 'movie':
            return '/movies';
          case 'music':
            return '/music';
          case 'book':
            return '/books';
          case 'board-game':
            return '/board-games';
          case 'trading-card':
            return '/trading-cards';
          case 'manga':
            return '/manga';
          case 'comic':
            return '/comics';
          default:
            return '/';
        }
      };

      navigate(getReturnRoute(), { replace: true });
    } catch (error) {
      console.error(`Error deleting ${itemType}:`, error);
      enqueueSnackbar(`Error removing ${itemType}: ${error.message}`, { variant: 'error' });
      setDeleteDialogOpen(false);
    }
  };

  // Format currency value
  const formatCurrency = (value) => {
    if (!value) return '£0.00';
    
    // If the value is already a formatted string (like from PriceCharting), return it as is
    if (typeof value === 'string' && value.includes('PriceCharting')) {
      return value;
    }
    
    // Try to extract a numeric value
    let numericValue;
    if (typeof value === 'string') {
      // Extract numeric value from string (e.g., "£10.99" -> 10.99)
      const match = value.match(/[\d.]+/);
      numericValue = match ? parseFloat(match[0]) : 0;
    } else {
      numericValue = parseFloat(value) || 0;
    }
    
    return `£${numericValue.toFixed(2)}`;
  };

  // Get secure image URL
  const getSecureImageUrl = (url) => {
    if (!url) return 'https://placehold.co/600x400?text=No+Image';
    
    // If URL is already HTTPS, return it
    if (url.startsWith('https://')) {
      return url;
    }
    
    // If URL is HTTP, proxy it through our backend
    if (url.startsWith('http://')) {
      return `/api/image-proxy?url=${encodeURIComponent(url)}`;
    }
    
    // If URL is relative, assume it's already secure
    return url;
  };

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <div>
        <AppBarHeader 
          isAdmin={isAdmin} 
          userId={userId} 
          onLogout={handleLogout}
        />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={handleBack}
              sx={{ mb: 2 }}
            >
              Back to Collection
            </Button>
            <Typography variant="h5" color="error" gutterBottom>
              {error}
            </Typography>
          </Paper>
        </Container>
      </div>
    );
  }

  // Determine item type-specific details
  const renderItemTypeSpecificDetails = () => {
    if (!item) return null;

    switch (itemType) {
      case 'game':
        return (
          <>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Platform: {item.platform}
              </Typography>
              {item.releaseYear && (
                <Typography variant="subtitle1" gutterBottom>
                  Release Year: {item.releaseYear}
                </Typography>
              )}
              {item.genre && (
                <Typography variant="subtitle1" gutterBottom>
                  Genre: {item.genre}
                </Typography>
              )}
              {item.developer && (
                <Typography variant="subtitle1" gutterBottom>
                  Developer: {item.developer}
                </Typography>
              )}
              {item.publisher && item.publisher !== item.developer && (
                <Typography variant="subtitle1" gutterBottom>
                  Publisher: {item.publisher}
                </Typography>
              )}
              {item.region && (
                <Typography variant="subtitle1" gutterBottom>
                  Region: {item.region}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>Played:</Typography>
                {item.played ? (
                  <Tooltip title="Played">
                    <CheckCircleIcon color="success" />
                  </Tooltip>
                ) : (
                  <Tooltip title="Not Played">
                    <CancelIcon color="error" />
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>Completed:</Typography>
                {item.completed ? (
                  <Tooltip title="Completed">
                    <EmojiEventsIcon sx={{ color: 'gold' }} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Not Completed">
                    <CancelIcon color="error" />
                  </Tooltip>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Edition: {item.edition || 'Standard'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Condition: {item.condition || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Priority: {item.priority || 'Not set'}
              </Typography>
            </Grid>
          </>
        );
      
      case 'console':
        return (
          <>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Manufacturer: {item.manufacturer || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Model: {item.model || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Year: {item.year || 'Unknown'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Generation: {item.generation || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Version: {item.version || 'Not specified'}
              </Typography>
              {item.alternative_name && (
                <Typography variant="subtitle1" gutterBottom>
                  Alternative Name: {item.alternative_name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Condition: {item.condition || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Priority: {item.priority || 'Not set'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Box Included: {item.boxIncluded ? 'Yes' : 'No'}
              </Typography>
            </Grid>
            {(item.description || item.summary) && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                {item.description && (
                  <Typography variant="body1" paragraph>
                    <strong>Description:</strong> {item.description}
                  </Typography>
                )}
                {item.summary && (
                  <Typography variant="body1" paragraph>
                    <strong>Summary:</strong> {item.summary}
                  </Typography>
                )}
              </Grid>
            )}
          </>
        );
        
      case 'movie':
        return (
          <>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Format: {item.format || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Year: {item.year || 'Unknown'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Director: {item.director || 'Not specified'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Condition: {item.condition || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Genre: {item.genre || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Watched: {item.watched ? 'Yes' : 'No'}
              </Typography>
            </Grid>
          </>
        );
      
      case 'lego':
        // Log the item data to help debug field names
        console.log('Rendering Lego item details:', item);
        console.log('Lego item field check:', {
          setNum: item.setNum,
          setNumber: item.setNumber,
          themeId: item.themeId,
          theme_id: item.theme_id,
          themeName: item.themeName,
          theme: item.theme,
          set_img_url: item.set_img_url,
          imageUrl: item.imageUrl
        });
        
        // Get set number from any available field
        const setNumber = item.setNum || item.setNumber || item.set_num || 'Not specified';
        // Get theme from any available field
        const theme = item.themeName || item.theme || item.themeId || item.theme_id || 'Not specified';
        // Get image URL from any available field
        const imageUrl = item.set_img_url || item.imageUrl || '';
        
        // Log the theme-related fields to help debug
        console.log('Theme-related fields:', {
          themeName: item.themeName,
          theme: item.theme,
          themeId: item.themeId,
          theme_id: item.theme_id,
          resolvedTheme: theme
        });
        
        return (
          <>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Set Number: {setNumber ? setNumber.split('-')[0] : 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Theme: {theme}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Theme ID: {item.themeId || item.theme_id || item.theme || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Year: {item.year || 'Unknown'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Condition: {item.condition || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Priority: {item.priority || 'Not set'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Pieces: {item.numParts || 'Unknown'}
              </Typography>
            </Grid>
            {(item.description || item.summary) && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                {item.description && (
                  <Typography variant="body1" paragraph>
                    <strong>Description:</strong> {item.description}
                  </Typography>
                )}
                {item.summary && (
                  <Typography variant="body1" paragraph>
                    <strong>Summary:</strong> {item.summary}
                  </Typography>
                )}
              </Grid>
            )}
          </>
        );

      case 'book':
        return (
          <>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Author: {item.author || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Format: {item.format || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Year: {item.year || 'Unknown'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Genre: {item.genre || 'Not specified'}
              </Typography>
              {item.series && (
                <Typography variant="subtitle1" gutterBottom>
                  Series: {item.series}{item.seriesNumber ? ` #${item.seriesNumber}` : ''}
                </Typography>
              )}
              {item.publisher && (
                <Typography variant="subtitle1" gutterBottom>
                  Publisher: {item.publisher}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Condition: {item.condition || 'Not specified'}
              </Typography>
              {item.edition && (
                <Typography variant="subtitle1" gutterBottom>
                  Edition: {item.edition}
                </Typography>
              )}
              {item.pages && (
                <Typography variant="subtitle1" gutterBottom>
                  Pages: {item.pages}
                </Typography>
              )}
              {item.isbn13 && (
                <Typography variant="subtitle1" gutterBottom>
                  ISBN-13: {item.isbn13}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>Read:</Typography>
                {item.read ? (
                  <Tooltip title="Read">
                    <CheckCircleIcon color="success" />
                  </Tooltip>
                ) : (
                  <Tooltip title="Not Read">
                    <CancelIcon color="error" />
                  </Tooltip>
                )}
              </Box>
            </Grid>
          </>
        );

      case 'music':
        return (
          <>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Artist: {item.artist || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Format: {item.format || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Year: {item.year || 'Unknown'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Genre: {item.genre || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Label: {item.label || 'Not specified'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Condition: {item.condition || 'Not specified'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Edition: {item.edition || 'Not specified'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>Listened:</Typography>
                {item.listened ? (
                  <Tooltip title="Listened">
                    <CheckCircleIcon color="success" />
                  </Tooltip>
                ) : (
                  <Tooltip title="Not Listened">
                    <CancelIcon color="error" />
                  </Tooltip>
                )}
              </Box>
            </Grid>
            {item.tracks && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" paragraph>
                  <strong>Track List:</strong>
                </Typography>
                <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                  {item.tracks}
                </Typography>
              </Grid>
            )}
          </>
        );

      case 'board-game':
        return (
          <>
            <Grid item xs={12} md={6}>
              {item.designers && (
                <Typography variant="subtitle1" gutterBottom>
                  Designer(s): {item.designers}
                </Typography>
              )}
              {item.publisher && (
                <Typography variant="subtitle1" gutterBottom>
                  Publisher: {item.publisher}
                </Typography>
              )}
              {item.category && (
                <Typography variant="subtitle1" gutterBottom>
                  Category: {item.category}
                </Typography>
              )}
              {item.genre && item.genre !== item.category && (
                <Typography variant="subtitle1" gutterBottom>
                  Genre/Mechanic: {item.genre}
                </Typography>
              )}
              {item.year && (
                <Typography variant="subtitle1" gutterBottom>
                  Year: {item.year}
                </Typography>
              )}
              {item.language && (
                <Typography variant="subtitle1" gutterBottom>
                  Language: {item.language}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {(item.minPlayers || item.maxPlayers) && (
                <Typography variant="subtitle1" gutterBottom>
                  Players: {item.minPlayers === item.maxPlayers
                    ? item.minPlayers
                    : `${item.minPlayers || '?'}–${item.maxPlayers || '?'}`}
                </Typography>
              )}
              {item.playingTime && (
                <Typography variant="subtitle1" gutterBottom>
                  Playing Time: {item.playingTime} min
                </Typography>
              )}
              {item.minAge && (
                <Typography variant="subtitle1" gutterBottom>
                  Min Age: {item.minAge}+
                </Typography>
              )}
              {item.condition && (
                <Typography variant="subtitle1" gutterBottom>
                  Condition: {item.condition}
                </Typography>
              )}
              {item.edition && (
                <Typography variant="subtitle1" gutterBottom>
                  Edition: {item.edition}
                </Typography>
              )}
              {item.price && (
                <Typography variant="subtitle1" gutterBottom>
                  Price Paid: {item.price}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>Played:</Typography>
                {item.played ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
              </Box>
            </Grid>
            {item.description && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" gutterBottom><strong>Description</strong></Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{item.description}</Typography>
              </Grid>
            )}
          </>
        );

      case 'trading-card':
        return (
          <>
            <Grid item xs={12} md={6}>
              {item.game && <Typography variant="subtitle1" gutterBottom>Game: {item.game}</Typography>}
              {item.setName && <Typography variant="subtitle1" gutterBottom>Set: {item.setName}</Typography>}
              {item.cardNumber && <Typography variant="subtitle1" gutterBottom>Card Number: #{item.cardNumber}</Typography>}
              {item.rarity && <Typography variant="subtitle1" gutterBottom>Rarity: {item.rarity}</Typography>}
              {item.type && <Typography variant="subtitle1" gutterBottom>Type: {item.type}</Typography>}
              {item.artist && <Typography variant="subtitle1" gutterBottom>Artist: {item.artist}</Typography>}
            </Grid>
            <Grid item xs={12} md={6}>
              {item.condition && <Typography variant="subtitle1" gutterBottom>Condition: {item.condition}</Typography>}
              {item.language && <Typography variant="subtitle1" gutterBottom>Language: {item.language}</Typography>}
              {item.quantity > 1 && <Typography variant="subtitle1" gutterBottom>Quantity: {item.quantity}</Typography>}
              {item.game === 'Pokémon' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ mr: 1 }}>1st Edition:</Typography>
                    {item.isFirstEdition ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ mr: 1 }}>Holographic:</Typography>
                    {item.isHolo ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                  </Box>
                </>
              )}
              {item.game === 'Magic: The Gathering' && item.finish && (
                <Typography variant="subtitle1" gutterBottom>Finish: {item.finish}</Typography>
              )}
              {item.isGraded && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ mr: 1 }}>Graded:</Typography>
                    <CheckCircleIcon color="success" />
                  </Box>
                  {item.gradingCompany && <Typography variant="subtitle1" gutterBottom>Grading Company: {item.gradingCompany}</Typography>}
                  {item.grade != null && <Typography variant="subtitle1" gutterBottom>Grade: {item.grade}</Typography>}
                </>
              )}
              {item.price && <Typography variant="subtitle1" gutterBottom>Price Paid: {item.price}</Typography>}
            </Grid>
          </>
        );

      case 'manga':
        return (
          <>
            <Grid item xs={12} md={6}>
              {item.author && <Typography variant="subtitle1" gutterBottom>Author/Mangaka: {item.author}</Typography>}
              {item.publisher && <Typography variant="subtitle1" gutterBottom>Publisher: {item.publisher}</Typography>}
              <Typography variant="subtitle1" gutterBottom>
                Volume: {item.volume}{item.totalVolumes ? ` of ${item.totalVolumes}` : ''}
              </Typography>
              {item.demographic && <Typography variant="subtitle1" gutterBottom>Demographic: {item.demographic}</Typography>}
              {item.format && <Typography variant="subtitle1" gutterBottom>Format: {item.format}</Typography>}
              {item.genre && <Typography variant="subtitle1" gutterBottom>Genre: {item.genre}</Typography>}
              {item.year && <Typography variant="subtitle1" gutterBottom>Year: {item.year}</Typography>}
            </Grid>
            <Grid item xs={12} md={6}>
              {item.condition && <Typography variant="subtitle1" gutterBottom>Condition: {item.condition}</Typography>}
              {item.language && <Typography variant="subtitle1" gutterBottom>Language: {item.language}</Typography>}
              {item.isbn13 && <Typography variant="subtitle1" gutterBottom>ISBN-13: {item.isbn13}</Typography>}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>Read:</Typography>
                {item.read ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
              </Box>
              {item.price && <Typography variant="subtitle1" gutterBottom>Price Paid: {item.price}</Typography>}
            </Grid>
          </>
        );

      case 'comic':
        return (
          <>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Series: {item.series}</Typography>
              <Typography variant="subtitle1" gutterBottom>Issue: #{item.issueNumber}</Typography>
              {item.publisher && <Typography variant="subtitle1" gutterBottom>Publisher: {item.publisher}</Typography>}
              {item.year && <Typography variant="subtitle1" gutterBottom>Cover Year: {item.year}</Typography>}
              {item.writer && <Typography variant="subtitle1" gutterBottom>Writer: {item.writer}</Typography>}
              {item.artist && <Typography variant="subtitle1" gutterBottom>Artist / Penciler: {item.artist}</Typography>}
              {item.storyArc && <Typography variant="subtitle1" gutterBottom>Story Arc: {item.storyArc}</Typography>}
              {item.coverVariant && <Typography variant="subtitle1" gutterBottom>Cover Variant: {item.coverVariant}</Typography>}
            </Grid>
            <Grid item xs={12} md={6}>
              {item.condition && <Typography variant="subtitle1" gutterBottom>Condition: {item.condition}</Typography>}
              {item.isFirstPrint && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ mr: 1 }}>1st Print:</Typography>
                  <CheckCircleIcon color="success" />
                </Box>
              )}
              {item.isGraded && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ mr: 1 }}>Graded Slab:</Typography>
                    <CheckCircleIcon color="success" />
                  </Box>
                  {item.gradingCompany && <Typography variant="subtitle1" gutterBottom>Grading Company: {item.gradingCompany}</Typography>}
                  {item.grade != null && <Typography variant="subtitle1" gutterBottom>Grade: {item.grade}</Typography>}
                </>
              )}
              {item.price && <Typography variant="subtitle1" gutterBottom>Price Paid: {item.price}</Typography>}
              {item.priority && <Typography variant="subtitle1" gutterBottom>Priority: {item.priority}</Typography>}
            </Grid>
            {item.isSold && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Sold</Typography>
                {item.soldPrice != null && (
                  <Typography variant="subtitle1" gutterBottom>Sold For: £{item.soldPrice}</Typography>
                )}
                {item.soldDate && (
                  <Typography variant="subtitle1" gutterBottom>
                    Sold On: {new Date(item.soldDate).toLocaleDateString()}
                  </Typography>
                )}
              </Grid>
            )}
          </>
        );

      default:
        return null;
    }
  };

  // Get edit fields based on item type
  const getEditFields = () => {
    switch (itemType) {
      case 'game':
        return gameEditFields;
      case 'console':
        return [
          {
            name: 'name',
            label: 'Name',
            type: 'text',
            required: true,
            gridProps: { xs: 12 }
          },
          {
            name: 'manufacturer',
            label: 'Manufacturer',
            type: 'text',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'model',
            label: 'Model',
            type: 'text',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'year',
            label: 'Year',
            type: 'number',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'generation',
            label: 'Generation',
            type: 'select',
            options: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'version',
            label: 'Version',
            type: 'text',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'condition',
            label: 'Condition',
            type: 'select',
            options: ['Mint (M)', 'Near Mint (NM)', 'Excellent (EX)', 'Very Good (VG)', 'Good (G)', 'Fair (F)', 'Poor (P)', 'For Parts or Not Working'],
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'alternative_name',
            label: 'Alternative Name',
            type: 'text',
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'boxIncluded',
            label: 'Box Included',
            type: 'checkbox',
            required: false,
            gridProps: { xs: 6 }
          },
          {
            name: 'priority',
            label: 'Priority',
            type: 'select',
            options: ['Low', 'Medium', 'High'],
            required: false,
            gridProps: { xs: 6 }
          },
          {
            name: 'description',
            label: 'Description',
            type: 'text',
            multiline: true,
            rows: 4,
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'summary',
            label: 'Summary',
            type: 'text',
            multiline: true,
            rows: 4,
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'rating',
            label: 'Rating',
            type: 'rating',
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'notes',
            label: 'Notes',
            type: 'text',
            multiline: true,
            rows: 4,
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'platformLogo',
            label: 'List Logo URL',
            helperText: 'Image shown in list views',
            type: 'text',
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'imageUrl',
            label: 'Image URL',
            helperText: 'Image shown on the details page',
            type: 'text',
            required: false,
            gridProps: { xs: 12 }
          }
        ];
      case 'lego':
        console.log('Getting edit fields for Lego item:', item);
        
        // Prepare the item data for the form
        const preparedItem = { ...item };
        
        // Ensure both setNum and setNumber are available
        if (preparedItem.setNumber && !preparedItem.setNum) {
          preparedItem.setNum = preparedItem.setNumber;
        } else if (preparedItem.setNum && !preparedItem.setNumber) {
          preparedItem.setNumber = preparedItem.setNum;
        }
        
        // Ensure both imageUrl and set_img_url are available
        if (preparedItem.imageUrl && !preparedItem.set_img_url) {
          preparedItem.set_img_url = preparedItem.imageUrl;
        } else if (preparedItem.set_img_url && !preparedItem.imageUrl) {
          preparedItem.imageUrl = preparedItem.set_img_url;
        }
        
        // Ensure both theme and themeId are available
        if (preparedItem.theme && !preparedItem.themeId) {
          console.log(`Adding themeId field with value: ${preparedItem.theme}`);
          preparedItem.themeId = preparedItem.theme;
        } else if (preparedItem.themeId && !preparedItem.theme) {
          console.log(`Adding theme field with value: ${preparedItem.themeId}`);
          preparedItem.theme = preparedItem.themeId;
        }
        
        console.log('Prepared Lego item for edit form:', {
          setNum: preparedItem.setNum,
          setNumber: preparedItem.setNumber,
          themeId: preparedItem.themeId,
          theme: preparedItem.theme,
          set_img_url: preparedItem.set_img_url,
          imageUrl: preparedItem.imageUrl
        });
        
        // Update the item state with the prepared data
        if (JSON.stringify(item) !== JSON.stringify(preparedItem)) {
          setItem(preparedItem);
        }
        
        return [
          {
            name: 'name',
            label: 'Name',
            type: 'text',
            required: true,
            gridProps: { xs: 12 }
          },
          {
            name: 'setNum',
            label: 'Set Number',
            type: 'text',
            required: true,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'year',
            label: 'Year',
            type: 'number',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'themeId',
            label: 'Theme ID',
            type: 'text',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'priority',
            label: 'Priority',
            type: 'select',
            options: ['Low', 'Medium', 'High'],
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'set_img_url',
            label: 'Image URL',
            type: 'text',
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'condition',
            label: 'Condition',
            type: 'select',
            options: ['New', 'Used', 'Complete', 'Incomplete', 'Sealed', 'Opened'],
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'numParts',
            label: 'Number of Pieces',
            type: 'number',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'rating',
            label: 'Rating',
            type: 'rating',
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'notes',
            label: 'Notes',
            type: 'text',
            multiline: true,
            rows: 4,
            required: false,
            gridProps: { xs: 12 }
          }
        ];
      case 'movie':
        return [
          {
            name: 'name',
            label: 'Movie Title',
            type: 'text',
            required: true,
            gridProps: { xs: 12 }
          },
          {
            name: 'format',
            label: 'Format',
            type: 'select',
            options: ['DVD', 'Blu-ray', '4K Ultra HD', 'VHS', 'LaserDisc', 'Digital', 'Other'],
            required: true,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'year',
            label: 'Year',
            type: 'number',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'director',
            label: 'Director',
            type: 'text',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'genre',
            label: 'Genre',
            type: 'text',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'watched',
            label: 'Watched',
            type: 'checkbox',
            required: false,
            gridProps: { xs: 6 }
          },
          {
            name: 'condition',
            label: 'Condition',
            type: 'select',
            options: ['Brand New (BNIB / NIB)', 'New Old Stock (NOS)', 'Factory Sealed', 'Mint Condition (MINT)', 'Like New', 'Complete in Box (CIB)', 'Box Only / Case Only', 'Disc Only (Loose)', 'Good / Acceptable', 'Damaged Case / Cover', 'For Parts / Not Working'],
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'rating',
            label: 'Rating',
            type: 'rating',
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'imageUrl',
            label: 'Image URL',
            type: 'text',
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'notes',
            label: 'Notes',
            type: 'text',
            multiline: true,
            rows: 4,
            required: false,
            gridProps: { xs: 12 }
          }
        ];
      case 'book':
        return [
          { name: 'name', label: 'Title', type: 'text', required: true, gridProps: { xs: 12 } },
          { name: 'author', label: 'Author', type: 'text', required: true, gridProps: { xs: 12, sm: 6 } },
          {
            name: 'format', label: 'Format', type: 'select', required: true,
            options: ['Hardback', 'Paperback', 'eBook', 'Audiobook', 'Leather Bound', 'Board Book', 'Other'],
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'condition', label: 'Condition', type: 'select', required: false,
            options: ['Mint (M)', 'Near Mint (NM)', 'Very Good (VG)', 'Good (G)', 'Fair (F)', 'Poor (P)', 'Ex-Library', 'Book Club Edition', 'First Edition'],
            gridProps: { xs: 12, sm: 6 }
          },
          { name: 'edition', label: 'Edition', type: 'text', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'year', label: 'Year', type: 'number', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'genre', label: 'Genre', type: 'text', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'publisher', label: 'Publisher', type: 'text', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'pages', label: 'Pages', type: 'number', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'series', label: 'Series', type: 'text', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'seriesNumber', label: 'Book # in Series', type: 'number', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'isbn13', label: 'ISBN-13', type: 'text', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'read', label: 'Read', type: 'checkbox', required: false, gridProps: { xs: 6 } },
          { name: 'price', label: 'Price Paid', type: 'text', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'rating', label: 'Rating', type: 'rating', required: false, gridProps: { xs: 12 } },
          { name: 'imageUrl', label: 'Cover Image URL', type: 'text', required: false, gridProps: { xs: 12 } },
          { name: 'notes', label: 'Notes', type: 'text', multiline: true, rows: 4, required: false, gridProps: { xs: 12 } }
        ];
      case 'music':
        return [
          {
            name: 'name',
            label: 'Album Title',
            type: 'text',
            required: true,
            gridProps: { xs: 12 }
          },
          {
            name: 'artist',
            label: 'Artist',
            type: 'text',
            required: true,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'format',
            label: 'Format',
            type: 'select',
            options: ['Vinyl', 'CD', 'Cassette', '8-Track', 'Digital', 'Box Set', 'Other'],
            required: true,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'condition',
            label: 'Condition',
            type: 'select',
            options: ['Mint (M)', 'Near Mint (NM)', 'Very Good Plus (VG+)', 'Very Good (VG)', 'Good (G)', 'Poor (P)', 'Factory Sealed', 'New', 'Like New', 'Good / Acceptable', 'For Parts / Not Working'],
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'edition',
            label: 'Edition',
            type: 'select',
            options: ['Standard Release', 'Limited Edition', 'First Pressing', 'Reissue', 'Remastered', 'Deluxe Edition', "Collector's Edition", 'Anniversary Edition', 'Promotional Copy', 'Import', 'Colored Vinyl', 'Picture Disc', 'Box Set', 'Not Specified'],
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'year',
            label: 'Year',
            type: 'number',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'genre',
            label: 'Genre',
            type: 'text',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'label',
            label: 'Record Label',
            type: 'text',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'listened',
            label: 'Listened',
            type: 'checkbox',
            required: false,
            gridProps: { xs: 6 }
          },
          {
            name: 'price',
            label: 'Price Paid',
            type: 'text',
            required: false,
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'tracks',
            label: 'Track List',
            type: 'text',
            multiline: true,
            rows: 4,
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'rating',
            label: 'Rating',
            type: 'rating',
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'imageUrl',
            label: 'Image URL',
            type: 'text',
            required: false,
            gridProps: { xs: 12 }
          },
          {
            name: 'notes',
            label: 'Notes',
            type: 'text',
            multiline: true,
            rows: 4,
            required: false,
            gridProps: { xs: 12 }
          }
        ];
      case 'board-game':
        return [
          { name: 'name', label: 'Game Name', type: 'text', required: true, gridProps: { xs: 12 } },
          { name: 'designers', label: 'Designer(s)', type: 'text', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'publisher', label: 'Publisher', type: 'text', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'year', label: 'Year', type: 'number', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'minPlayers', label: 'Min Players', type: 'number', required: false, gridProps: { xs: 6, sm: 3 } },
          { name: 'maxPlayers', label: 'Max Players', type: 'number', required: false, gridProps: { xs: 6, sm: 3 } },
          { name: 'playingTime', label: 'Playing Time (min)', type: 'number', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'minAge', label: 'Min Age', type: 'number', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'category', label: 'Category', type: 'text', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'genre', label: 'Genre/Mechanic', type: 'text', required: false, gridProps: { xs: 12, sm: 6 } },
          {
            name: 'language', label: 'Language', type: 'select', required: false,
            options: ['English', 'French', 'German', 'Spanish', 'Italian', 'Other'],
            gridProps: { xs: 12, sm: 6 }
          },
          {
            name: 'condition', label: 'Condition', type: 'select', required: false,
            options: ['New Sealed', 'Like New', 'Very Good (VG)', 'Good (G)', 'Fair (F)', 'Poor (P)', 'Incomplete'],
            gridProps: { xs: 12, sm: 6 }
          },
          { name: 'edition', label: 'Edition', type: 'text', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'played', label: 'Played', type: 'checkbox', required: false, gridProps: { xs: 6 } },
          { name: 'favourite', label: 'Favourite', type: 'checkbox', required: false, gridProps: { xs: 6 } },
          { name: 'price', label: 'Price Paid', type: 'text', required: false, gridProps: { xs: 12, sm: 6 } },
          { name: 'rating', label: 'Rating', type: 'rating', required: false, gridProps: { xs: 12 } },
          { name: 'description', label: 'Description', type: 'text', multiline: true, rows: 4, required: false, gridProps: { xs: 12 } },
          { name: 'imageUrl', label: 'Image URL', type: 'text', required: false, gridProps: { xs: 12 } },
          { name: 'notes', label: 'Notes', type: 'text', multiline: true, rows: 4, required: false, gridProps: { xs: 12 } }
        ];

      case 'trading-card':
        return [
          { name: 'name', label: 'Card Name', type: 'text', required: true, gridProps: { xs: 12 } },
          { name: 'game', label: 'Game', type: 'select', required: true, options: ['Pokémon', 'Magic: The Gathering', 'Yu-Gi-Oh!', 'Other'], gridProps: { xs: 12, sm: 6 } },
          { name: 'setName', label: 'Set Name', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'cardNumber', label: 'Card Number', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'rarity', label: 'Rarity', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'condition', label: 'Condition', type: 'select', options: ['Near Mint', 'Lightly Played', 'Moderately Played', 'Heavily Played', 'Damaged'], gridProps: { xs: 12, sm: 6 } },
          { name: 'language', label: 'Language', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'quantity', label: 'Quantity', type: 'number', gridProps: { xs: 12, sm: 6 } },
          { name: 'artist', label: 'Artist', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'isGraded', label: 'Graded', type: 'checkbox', gridProps: { xs: 12, sm: 6 } },
          { name: 'gradingCompany', label: 'Grading Company', type: 'text', showWhen: 'isGraded', gridProps: { xs: 12, sm: 6 } },
          { name: 'grade', label: 'Grade', type: 'number', showWhen: 'isGraded', gridProps: { xs: 12, sm: 6 } },
          { name: 'price', label: 'Price Paid', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'rating', label: 'Rating', type: 'rating', gridProps: { xs: 12 } },
          { name: 'imageUrl', label: 'Image URL', type: 'text', gridProps: { xs: 12 } },
          { name: 'notes', label: 'Notes', type: 'textarea', gridProps: { xs: 12 } },
        ];

      case 'manga':
        return [
          { name: 'name', label: 'Series Title', type: 'text', required: true, gridProps: { xs: 12 } },
          { name: 'volume', label: 'Volume Number', type: 'number', required: true, gridProps: { xs: 12, sm: 6 } },
          { name: 'totalVolumes', label: 'Total Volumes', type: 'number', gridProps: { xs: 12, sm: 6 } },
          { name: 'author', label: 'Author/Mangaka', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'publisher', label: 'Publisher', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'year', label: 'Year', type: 'number', gridProps: { xs: 12, sm: 6 } },
          { name: 'demographic', label: 'Demographic', type: 'select', options: ['', 'Shonen', 'Shojo', 'Seinen', 'Josei', 'Kodomomuke', 'Other'], gridProps: { xs: 12, sm: 6 } },
          { name: 'format', label: 'Format', type: 'select', options: ['Tankōbon', 'Omnibus', 'Deluxe Edition', 'Box Set', 'Other'], gridProps: { xs: 12, sm: 6 } },
          { name: 'condition', label: 'Condition', type: 'select', options: ['Mint (M)', 'Near Mint (NM)', 'Very Good (VG)', 'Good (G)', 'Fair (F)', 'Poor (P)'], gridProps: { xs: 12, sm: 6 } },
          { name: 'read', label: 'Read', type: 'checkbox', gridProps: { xs: 12, sm: 6 } },
          { name: 'price', label: 'Price Paid', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'rating', label: 'Rating', type: 'rating', gridProps: { xs: 12 } },
          { name: 'imageUrl', label: 'Image URL', type: 'text', gridProps: { xs: 12 } },
          { name: 'notes', label: 'Notes', type: 'textarea', gridProps: { xs: 12 } },
        ];

      case 'comic':
        return [
          { name: 'series', label: 'Series Title', type: 'text', required: true, gridProps: { xs: 12 } },
          { name: 'issueNumber', label: 'Issue Number', type: 'text', required: true, gridProps: { xs: 12, sm: 6 } },
          { name: 'publisher', label: 'Publisher', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'year', label: 'Year', type: 'number', gridProps: { xs: 12, sm: 6 } },
          { name: 'writer', label: 'Writer', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'artist', label: 'Artist', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'storyArc', label: 'Story Arc', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'coverVariant', label: 'Cover Variant', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'condition', label: 'Condition', type: 'select', options: ['Mint (M)', 'Near Mint (NM)', 'Very Good (VG)', 'Good (G)', 'Fair (F)', 'Poor (P)'], gridProps: { xs: 12, sm: 6 } },
          { name: 'isFirstPrint', label: '1st Print', type: 'checkbox', gridProps: { xs: 12, sm: 6 } },
          { name: 'isGraded', label: 'Graded', type: 'checkbox', gridProps: { xs: 12, sm: 6 } },
          { name: 'gradingCompany', label: 'Grading Company', type: 'text', showWhen: 'isGraded', gridProps: { xs: 12, sm: 6 } },
          { name: 'grade', label: 'Grade', type: 'number', showWhen: 'isGraded', gridProps: { xs: 12, sm: 6 } },
          { name: 'price', label: 'Price Paid', type: 'text', gridProps: { xs: 12, sm: 6 } },
          { name: 'rating', label: 'Rating', type: 'rating', gridProps: { xs: 12 } },
          { name: 'imageUrl', label: 'Image URL', type: 'text', gridProps: { xs: 12 } },
          { name: 'notes', label: 'Notes', type: 'textarea', gridProps: { xs: 12 } },
        ];

      default:
        return [];
    }
  };

  // Derive display name — comics store series+issueNumber, not name
  const displayName = itemType === 'comic'
    ? (item?.series ? `${item.series} #${item.issueNumber}` : item?.name)
    : item?.name;

  // Check if we came from a wishlist page using location state
  const isWishlistPage = location.state?.fromWishlist || false;
  
  console.log('Current path:', location.pathname);
  console.log('Is wishlist page (from state):', isWishlistPage);
  
  return (
    <div>
      {console.log('ItemDetailsPage - item state:', item)} {/* DEBUGGING */}
      <AppBarHeader 
        isAdmin={isAdmin} 
        userId={userId} 
        onLogout={handleLogout}
      />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={handleBack}
            >
              {/* Show 'Back to Wishlist' for all wishlist items, otherwise show 'Back to Collection' */}
              {isWishlistPage ? 'Back to Wishlist' : 'Back to Collection'}
            </Button>
            <Box>
              {/* Add to Collection button - Only show for movie wishlist items */}
              {itemType === 'movie' && location.pathname.includes('wishlist') && (
                <Button 
                  onClick={handleMoveToCollection}
                  variant="contained"
                  color="success"
                  sx={{ mr: 1 }}
                >
                  Add to Collection
                </Button>
              )}
              <Button 
                startIcon={<EditIcon />} 
                onClick={handleEdit}
                variant="outlined"
                color="primary"
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              <Button 
                startIcon={<DeleteIcon />} 
                onClick={handleDeleteClick}
                variant="outlined"
                color="error"
              >
                Remove
              </Button>
            </Box>
          </Box>
          
          <Typography variant="h4" gutterBottom>
            {displayName}
          </Typography>
          
          <Grid container spacing={4}>
            {/* Item Image */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardMedia
                  component="img"
                  sx={{ 
                    height: 400, 
                    objectFit: 'contain', 
                    bgcolor: 'background.paper',
                    p: 2
                  }}
                  image={(() => {
                    const raw = item?.imageUrl || item?.processedImageUrl || item?.platformLogo || item?.set_img_url;
                    if (!raw) return '';
                    // Proxy MangaDex and ComicVine images through backend to bypass ISP blocks
                    if (raw.includes('mangadex.org') || raw.includes('comicvine.gamespot.com')) {
                      return `/api/proxy-image?url=${encodeURIComponent(raw)}`;
                    }
                    return getSecureImageUrl(raw);
                  })()}
                  alt={displayName}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://placehold.co/600x400?text=No+Image';
                  }}
                />
              </Card>
            </Grid>
            
            {/* Item Details */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Details
                  </Typography>
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    {renderItemTypeSpecificDetails()}
                    
                    {/* Value Information Section — eBay sold listings for all item types */}
                    {(
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="h6" gutterBottom>
                          <AttachMoneyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                          Value Information
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <EbayPriceValue
                            name={displayName}
                            type={itemType}
                            platform={item?.platform}
                            format={item?.format}
                            setNum={itemType === 'lego' ? item?.setNum : null}
                          />
                        </Box>
                      </Grid>
                    )}
                    
                    {/* Movie Additional Details Section - Only show for movies */}
                    {itemType === 'movie' && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="h6" gutterBottom>
                          Additional Details
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          {item?.runtime && (
                            <Typography variant="body1" gutterBottom>
                              <strong>Runtime:</strong> {item.runtime}
                            </Typography>
                          )}
                          {item?.plot && (
                            <Typography variant="body1" gutterBottom>
                              <strong>Plot:</strong> {item.plot}
                            </Typography>
                          )}
                          {item?.edition && (
                            <Typography variant="body1" gutterBottom>
                              <strong>Edition:</strong> {item.edition}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    )}
                    
                    {/* Notes Section */}
                    {item?.notes && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="h6" gutterBottom>
                          Notes
                        </Typography>
                        
                        <Typography variant="body1">
                          {item.notes}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Container maxWidth="lg" sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
        {featureFlags?.enableChatRooms && (
          <Button
            variant="outlined"
            startIcon={<ChatIcon />}
            onClick={() => navigate(`/chat?room=${chatRoom}`)}
            size="small"
          >
            Chat about this in #{chatRoom}
          </Button>
        )}
      </Container>

      <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
        <ReviewList
          collectionType={collectionType}
          itemId={itemId}
          currentUserId={userId}
          collectionItemId={!location.state?.fromWishlist ? itemId : null}
        />
        <ItemGallery
          collectionType={collectionType}
          itemId={itemId}
          currentUserId={userId}
          collectionItemId={!location.state?.fromWishlist ? itemId : null}
        />
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {`Delete ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {`Are you sure you want to delete ${displayName}? This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Dialog */}
      <EditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        item={item}
        onSave={handleSaveEdit}
        itemType={itemType}
        fields={getEditFields()}
      />
    </div>
  );
};

export default ItemDetailsPage;
